<template>
  <EquipmentRow data-cy="hps-motor-row-equipment-row">
    <template #left></template>

    <template #default="{ toggle }">
      <AddRemoveButtons
        :hide-add-below="true"
        :disable-remove="!equipment.motor_id"
        data-cy="hps-motor-row-add-remove-buttons"
        @remove="
          equipmentForm.update({
            r_motor: null,
            motor_id: null
          })
        "
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            :rule-names="[
              'motor_power',
              'startup_voltage',
              'flow_velocity_around_motor_low',
              'flow_velocity_around_motor_high',
              'flow_velocity_around_motor_high_abrasive',
              'motor_load_ratio',
              'motor_under_load_ratio',
              'motor_skin_temp',
              'motor_size_top_casing',
              'motor_size_bottom_casing',
              'motor_stage_temp',
              'motor_lock_plate',
              'motor_obsolete'
            ]"
            data-cy="hps-motor-row-equipment-panel"
            @toggle="toggle"
          >
            <template #header>Motor</template>
            <template v-if="equipment.r_motor" #trigger>
              {{ computeMotorLabel(equipment.r_motor) }}
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <div class="col-12 text-center">
        <HpsMotorSearchView
          :is-admin="false"
          :required-hp="requiredHp"
          :enable-hp-filter="true"
          data-cy="hps-motor-row-motor-search-view"
          @select="handleHPSMotorSelect($event, toggle)"
        />
      </div>
    </template>
    <template #right>
      <div
        class="flex align-items-center"
        style="gap: 1em"
        data-cy="hps-motor-row-right"
      >
        <ConverterInput
          label="Length"
          :model-value="equipment.r_motor?.length_inches || 0"
          :base-unit="LengthUnits.Inches"
          :target-unit="getLargeLengthUnit('Length.convert.Inches')"
          :model="LargeLengthUnits.options()"
          mode="convert"
          data-cy="hps-motor-row-length-converter"
        />
        <ConverterInput
          label="Weight"
          :model-value="equipment.r_motor?.weight_pounds || 0"
          :base-unit="MassUnits.Pounds"
          :model="MassUnits.options()"
          mode="convert"
        />
        <div class="flex flex-column" style="gap: 0.25em">
          <label class="text-xs text-color-secondary">Elevation (ft ASL)</label>
          <InputNumber
            :model-value="parameters.elevation_feet_asl ?? null"
            :min="0"
            :max="30000"
            :use-grouping="false"
            placeholder="ft ASL"
            data-cy="hps-motor-row-elevation-input"
            @update:model-value="handleElevationChange"
          />
        </div>
      </div>
    </template>
  </EquipmentRow>
</template>

<script setup lang="ts">
import ConverterInput from "@/components/ConverterInput.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import HpsMotorSearchView from "./HpsMotorSearchView.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import InputNumber from "primevue/inputnumber";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { injectStrict } from "@/utils/utils";
import { computed } from "vue";
import {
  equipmentFormKey,
  parametersFormKey,
  scenarioKey,
  sizingKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { computeMotorLabel } from "@/models/equipment/motors/properties";
import type { Motors } from "gql/api";

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const parametersForm = injectStrict(parametersFormKey);
const parameters = computed(() => parametersForm.data.value);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { motorCalculations } = injectStrict(scenarioCalculationsKey);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

const requiredHp = computed(() =>
  Math.ceil(motorCalculations.totalRequired60HzBrakeHP.value || 0)
);

function handleHPSMotorSelect(motor: Motors, toggle: () => void) {
  toggle();
  equipmentForm.update({
    r_motor: motor,
    motor_id: motor.id
  });
}

function handleElevationChange(value: number | null) {
  parametersForm.update({
    elevation_feet_asl: value
  } as Partial<typeof parametersForm.data.value>);
}
</script>
